// Sentry initialization & infrastructure — the one module allowed to drive the SDK directly.
// oxlint-disable no-restricted-imports
import * as Sentry from '@sentry/react';
import { makeFetchTransport } from '@sentry/react';
import {
  isErrorWithSentryContext,
  isModuleNotFoundError,
  isSentryCaptured,
  isSignalAbortError,
  markSentryCaptured,
} from '@/api/errors';
// oxlint-enable no-restricted-imports
import { BASE_URL } from '@/helpers/url';
import { router } from '@/router';
import { AD_BLOCKER_DETECTED } from '@/utils/constants';
import { isLocalDevelopmentEnv } from '@/utils/utils';
import { sentryContextIntegration } from './context-integration';

const earlyErrors = window.__earlyErrors ?? [];

// 1. Stop the early capture
if (window.__earlyHandler) {
  window.removeEventListener('error', window.__earlyHandler, true);
  window.removeEventListener('unhandledrejection', window.__earlyHandler);
}

// Sentry is enabled by default. Set VITE_SENTRY_DISABLED=true in local environments
// to prevent sending events to Sentry without removing any instrumentation code.
const isSentryDisabled = window.__QDRANT_CLOUD__.sentry.disabled === 'true';

const integrations = [
  Sentry.replayIntegration({
    networkDetailAllowUrls: [BASE_URL.toString()],
    networkResponseHeaders: ['X-Qd-Request-Id'],
    maskAllText: false,
  }),
  Sentry.tanstackRouterBrowserTracingIntegration(router, {
    /**
     * Decorate the auto `http.client` fetch span of each `/connect` call with our backend trace id,
     * so a UI interaction can be followed down to the backend gRPC call. This hook receives the raw
     * HTTP response headers, so the trace id appears either as a leading header (`qc-trace-id`) or
     * when sent as trailing metadata, prefixed with `trailer-`.
     * @see https://connectrpc.com/docs/protocol/#unary-response
     */
    onRequestSpanEnd(span, { headers }) {
      const traceId = headers?.get('qc-trace-id') ?? headers?.get('trailer-qc-trace-id');
      if (traceId) {
        span.setAttribute('qc.trace_id', traceId);
      }
    },
  }),
];

if (!isLocalDevelopmentEnv) {
  integrations.push(
    /**
     * Filter out errors originating from third parties, such as browser extensions, code-injecting browsers,
     * or widgets from third-party services that also use Sentry.
     * @see http://docs.sentry.io/platforms/javascript/configuration/filtering/#using-thirdpartyerrorfilterintegration
     */
    Sentry.thirdPartyErrorFilterIntegration({
      /** @see https://www.npmjs.com/package/@sentry/vite-plugin#applicationkey */
      filterKeys: [import.meta.env.PACKAGE_NAME],
      // eslint-disable-next-line @cspell/spellchecker
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
    }),
  );
}

// Enrich every event with our canonical tags/fingerprints in `preprocessEvent`, keeping `beforeSend`
// focused on the drop decisions it alone can make.
integrations.push(sentryContextIntegration(router));

// 2. Init Sentry
Sentry.init({
  // The last word on whether an event is sent: every enrichment (tags, fingerprints, route/domain
  // fallbacks) lives in `sentryContextIntegration`'s `preprocessEvent`, which runs first, so this
  // hook is left with only the drop decisions (the one thing `preprocessEvent` can't do).
  beforeSend(event, hint) {
    const { originalException, mechanism } = hint || {};
    // Ignore "onunhandledrejection" for failed dynamic imports to avoid duplicates handled by `createReactErrorHandler`.
    if (
      mechanism?.type === 'onunhandledrejection' &&
      mechanism?.handled === false &&
      isModuleNotFoundError(originalException)
    ) {
      return null;
    }
    // Drop intentional cancellations: a raw `AbortError` comes from code that called `abort()` on purpose
    // (query unmount, `cancelQueries`, Connect's post-response cleanup), so it is not a defect.
    if (isSignalAbortError(originalException)) {
      return null;
    }
    // Deduplicate captures of the same error instance. The centralized QueryClient handler and any
    // boundary that renders the same error (e.g. the not-found fallback) both call captureException.
    // Mark on first sight here and drop every subsequent capture of that instance, so it is reported
    // exactly once.
    if (originalException instanceof Error) {
      if (isSentryCaptured(originalException)) {
        return null;
      }
      // markSentryCaptured should only happen here because beforeSend runs after captureException returns,
      // so a synchronous mark at the call site would be set before this runs and would drop the first legitimate event.
      markSentryCaptured(originalException);
    }

    // Drop errors explicitly flagged to be ignored via their attached Sentry context.
    if (isErrorWithSentryContext(originalException) && originalException.sentryContext?.ignore) {
      return null;
    }

    if (isLocalDevelopmentEnv) {
      // Remove this part once we introduce the Sentry Dev Toolbar: https://docs.sentry.io/product/sentry-toolbar/
      // oxlint-disable-next-line no-console
      console.error('Sentry Event:', event);
    }

    return event;
  },
  beforeSendTransaction(event) {
    if (event.request?.url?.endsWith('/settings.json')) {
      return null;
    }
    return event;
  },
  // Done thru web UI, see: https://qdrant.sentry.io/settings/projects/cloud-ui/filters/data-filters/
  // It seems to not filter everything, so we specify some filters here as well:
  denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /safari-(web-)?extension:/i],
  dsn: window.__QDRANT_CLOUD__.sentry.dsn,
  enabled: !isSentryDisabled,
  environment: window.__QDRANT_CLOUD__.env,
  /**
   * Filter errors.
   * @see https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
   */
  ignoreErrors: [
    // https://apps.apple.com/us/app/microsoft-edge-browse-with-ai/id1288723196
    "Can't find variable: msDiscoverChatAvailable",
  ],
  initialScope: {
    tags: {
      /**
       * Detects if user has adblocker enabled - the p8network.js script is injected into index.html,
       * which in turn injects a div with id="..." into the DOM.
       * @see https://www.detectadblock.com
       */
      adblocker: AD_BLOCKER_DETECTED,
    },
  },
  integrations,
  release: `cloud-ui@${window.__QDRANT_CLOUD__.version}`,
  replaysOnErrorSampleRate: parseFloat(window.__QDRANT_CLOUD__.sentry.replays_on_error_sample_rate),
  replaysSessionSampleRate: parseFloat(window.__QDRANT_CLOUD__.sentry.replays_session_sample_rate),
  // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
  tracesSampleRate: parseFloat(window.__QDRANT_CLOUD__.sentry.traces_sample_rate),
  transport: (options) =>
    makeFetchTransport({
      ...options,
      fetchOptions: {
        keepalive: true,
        ...options.fetchOptions,
      },
    }),
});

// 3. Re-play the errors
earlyErrors.forEach((entry) => {
  if (entry.type === 'resource') {
    Sentry.captureMessage(`Resource failed to load: ${entry.url}`, {
      level: entry.url?.includes(BASE_URL.hostname) ? 'fatal' : 'warning',
      tags: { element: entry.element, context: 'early', type: entry.type },
    });
  } else {
    // Sentry is smart enough to extract stack traces from the 'error' property
    Sentry.captureException(entry.error, {
      level: 'fatal',
      tags: { context: 'early', type: entry.type },
    });
  }
});

// 4. Wipe references
delete window.__earlyErrors;
delete window.__earlyHandler;
